import { IEloginResponseAdapter, loginResponseAdapter } from '@api/FW/adapters/login.adapter';
import { getQueryParams, httpApi } from '@api/FW/http.api';
import { readLocalStorageData } from '@app/services/localStorage.service';
import { LocalStorageItems } from '@app/shared/constants/localStorageItems';
import { UserModel } from '@interfaces/FW/UserModel';
import md5 from 'md5';

export interface LoginRequest {
	username: string;
	password: string;
}

export interface LoginResponse {
	token: string;
	needSecondStep: boolean;
	user: UserModel | null;
	isAuth: boolean;
	emptyFinancialPhones: number;
	OilWalletsUserId: string;
}

export const login = (loginPayload: LoginRequest): Promise<LoginResponse> =>
	httpApi
		.post<IEloginResponseAdapter>(
			'Login',
			{
				User: loginPayload.username,
				Password: md5(loginPayload.password).toUpperCase(),
			},
			{ timeout: 10000, params: getQueryParams() },
		)
		.then(({ data }) => {
			return loginResponseAdapter(data);
		})
		.catch((err) => {
			throw err;
		});

export interface LoginPreRequest {
	/** Wire format — digits with country code, no `+` (e.g. `380501234488`). */
	phone: string;
}

/**
 * Login by phone, step 1 — sends a one-time code to the given number.
 * Responds `200` with an empty body; the backend rejects a resend issued
 * less than 60 seconds after the previous one.
 */
export const loginPre = (payload: LoginPreRequest): Promise<void> =>
	httpApi
		.post('LoginPre', { Phone: payload.phone }, { params: getQueryParams() })
		.then(() => undefined)
		.catch((err) => {
			throw err;
		});

export interface LoginByPhoneRequest extends LoginPreRequest {
	otp: string;
}

/**
 * Login by phone, step 2 — verifies the code from `loginPre` and issues a
 * session. Responds with the same body shape as `Login`.
 */
export const loginAuth = (payload: LoginByPhoneRequest): Promise<LoginResponse> =>
	httpApi
		.post<IEloginResponseAdapter>(
			'LoginAuth',
			{ Phone: payload.phone, OTP: payload.otp },
			{ params: getQueryParams() },
		)
		.then(({ data }) => loginResponseAdapter(data))
		.catch((err) => {
			throw err;
		});

export interface LoginAcceptRequest {
	verification: string;
}

export const loginAccept = (loginPayload: LoginAcceptRequest): Promise<LoginResponse> =>
	httpApi
		.post<IEloginResponseAdapter>(
			'LoginAccept',
			{
				SessionID: readLocalStorageData(LocalStorageItems.sessionID),
				SMSKey: md5(loginPayload.verification).toUpperCase(),
			},
			{ params: getQueryParams() },
		)
		.then(({ data }) => {
			const adapted = data?.Roles ? loginResponseAdapter(data) : null;

			return {
				isAuth: true,
				token: adapted?.token ?? '',
				needSecondStep: false,
				user: adapted?.user ?? null,
				emptyFinancialPhones: adapted?.emptyFinancialPhones ?? 0,
				OilWalletsUserId: adapted?.OilWalletsUserId ?? '',
			};
		})
		.catch((err) => {
			throw err;
		});
